import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

interface Span
{
  from: number;
  to: number;
}

interface Bounds
{
  x0: number; x1: number;
  y0: number; y1: number;
  z0: number; z1: number;
}

export class BrickMasonry
{
  private static readonly COURSES_PER_CELL = 3;
  private static readonly JOINT = 0.032;
  private static readonly EDGE_RADIUS = 0.04;
  private static readonly MORTAR_RECESS = BrickMasonry.EDGE_RADIUS - 0.002;
  private static readonly MIN_SPAN = 0.3;
  private static readonly LAYOUTS = 3;
  private static readonly EPSILON = 1e-6;

  private static readonly CLAY = [ 0xd8553a, 0xcf4a32, 0xe2643f, 0xc2412d, 0xea7446, 0xd24e35 ];
  private static readonly MORTAR = 0x5c372c;

  private static readonly geometries = new Map<string, THREE.BufferGeometry>();
  private static material: THREE.MeshPhongMaterial | null = null;

  public static getGeometry( width: number, height: number ): THREE.BufferGeometry
  {
    const layout = Math.floor( Math.random() * BrickMasonry.LAYOUTS );
    const key = `${width}x${height}#${layout}`;
    let geometry = BrickMasonry.geometries.get( key );

    if ( !geometry )
    {
      geometry = BrickMasonry.build( width, height, layout );
      BrickMasonry.geometries.set( key, geometry );
    }

    return geometry;
  }

  public static getMaterial(): THREE.Material
  {
    BrickMasonry.material ??= new THREE.MeshPhongMaterial( {
      vertexColors: true,
      color: 0xffffff,
      emissive: new THREE.Color( 0x2a0d06 ),
      shininess: 12,
      specular: new THREE.Color( 0x1c1c1c ),
      side: THREE.FrontSide
    } );

    return BrickMasonry.material;
  }

  private static build( width: number, height: number, layout: number ): THREE.BufferGeometry
  {
    const random = BrickMasonry.seededRandom( layout * 7919 + Math.round( width * 64 ) * 131 + Math.round( height * 64 ) );
    const courses = Math.max( 1, Math.round( height * BrickMasonry.COURSES_PER_CELL ) );
    const course_height = height / courses;
    const parts: THREE.BufferGeometry[] = [];

    for ( let course = 0; course < courses; course++ )
    {
      const y0 = course * course_height;
      const y1 = y0 + course_height;
      const header_course = ( course + layout ) % 2 === 1;
      const spans = BrickMasonry.splitCourse( width, header_course ? 0.5 : 1 );

      spans.forEach( ( span, index ) =>
      {
        const at_end = index === 0 || index === spans.length - 1;
        const is_header = header_course && at_end && span.to - span.from <= 0.5 + BrickMasonry.EPSILON;

        if ( is_header )
        {
          parts.push( BrickMasonry.brick( { x0: span.from, x1: span.to, y0, y1, z0: 0, z1: 1 }, width, height, random ) );
          return;
        }

        parts.push( BrickMasonry.brick( { x0: span.from, x1: span.to, y0, y1, z0: 0, z1: 0.5 }, width, height, random ) );
        parts.push( BrickMasonry.brick( { x0: span.from, x1: span.to, y0, y1, z0: 0.5, z1: 1 }, width, height, random ) );
      } );
    }

    parts.push( BrickMasonry.mortarCore( width, height ) );

    const merged = mergeGeometries( parts );
    parts.forEach( part => part.dispose() );
    if ( !merged )
      throw new Error( `[BrickMasonry] Failed to merge ${width}x${height} masonry` );

    merged.translate( -width * 0.5, -height * 0.5, -0.5 );
    merged.computeBoundingBox();
    merged.computeBoundingSphere();
    return merged;
  }

  private static splitCourse( length: number, first_cut: number ): Span[]
  {
    const cuts = [ 0 ];
    for ( let cut = first_cut; cut < length - BrickMasonry.EPSILON; cut += 1 )
      cuts.push( cut );
    cuts.push( length );

    const spans: Span[] = [];
    for ( let i = 1; i < cuts.length; i++ )
      spans.push( { from: cuts[i - 1], to: cuts[i] } );

    for ( let i = 0; i < spans.length && spans.length > 1; )
    {
      if ( spans[i].to - spans[i].from >= BrickMasonry.MIN_SPAN )
      {
        i++;
        continue;
      }

      if ( i === 0 )
        spans[1].from = spans[0].from;
      else
        spans[i - 1].to = spans[i].to;
      spans.splice( i, 1 );
    }

    return spans;
  }

  private static brick( cell: Bounds, width: number, height: number, random: () => number ): THREE.BufferGeometry
  {
    const half_joint = BrickMasonry.JOINT * 0.5;
    const eps = BrickMasonry.EPSILON;
    const x0 = cell.x0 > eps ? cell.x0 + half_joint : cell.x0;
    const x1 = cell.x1 < width - eps ? cell.x1 - half_joint : cell.x1;
    const y0 = cell.y0 > eps ? cell.y0 + half_joint : cell.y0;
    const y1 = cell.y1 < height - eps ? cell.y1 - half_joint : cell.y1;
    const z0 = cell.z0 > eps ? cell.z0 + half_joint : cell.z0;
    const z1 = cell.z1 < 1 - eps ? cell.z1 - half_joint : cell.z1;

    const outer = [
      cell.x0 <= eps, cell.x1 >= width - eps,
      cell.y0 <= eps, cell.y1 >= height - eps,
      cell.z0 <= eps, cell.z1 >= 1 - eps,
    ];
    const geometry = BrickMasonry.chamferedBox( x0, x1, y0, y1, z0, z1, BrickMasonry.EDGE_RADIUS, outer );

    const clay = new THREE.Color().setHex( BrickMasonry.CLAY[Math.floor( random() * BrickMasonry.CLAY.length )] );
    clay.offsetHSL( ( random() - 0.5 ) * 0.012, ( random() - 0.5 ) * 0.08, ( random() - 0.5 ) * 0.05 );

    // Color only depends on y and every face is planar, so per-vertex shading
    // interpolates to the same bottom-to-top gradient the dense mesh had.
    const position = geometry.getAttribute( "position" );
    const shade = new THREE.Color();
    geometry.setAttribute( "color", BrickMasonry.colorAttribute( position.count, i =>
      shade.copy( clay ).multiplyScalar( 0.88 + 0.16 * ( position.getY( i ) - y0 ) / ( y1 - y0 ) ) ) );
    return geometry;
  }

  /**
   * Box with chamfered edges: 6 face quads, 12 edge quads, 8 corner triangles —
   * 44 triangles on 24 indexed vertices. Every vertex carries the normal of the
   * face it belongs to, so a chamfer shades from one face's normal to the
   * other's and reads as a rounded edge, like RoundedBoxGeometry(segments=1)
   * did with 108 triangles on 324 vertices.
   */
  private static chamferedBox(
    x0: number, x1: number, y0: number, y1: number, z0: number, z1: number,
    radius: number,
    outer: boolean[]
  ): THREE.BufferGeometry
  {
    const r = Math.min( radius, ( x1 - x0 ) * 0.5, ( y1 - y0 ) * 0.5, ( z1 - z0 ) * 0.5 );
    const lo = [ x0, y0, z0 ];
    const hi = [ x1, y1, z1 ];
    const positions: number[] = [];
    const normals: number[] = [];

    // Vertex of face (axis, side) at the corner (sx, sy, sz): on the face plane,
    // pulled in by r along the two other axes.
    const vertex_index = ( axis: number, side: number, corner: number[] ): number =>
      ( axis * 2 + side ) * 4 + ( ( axis === 0 ? corner[1] : corner[0] ) * 2 + ( axis === 2 ? corner[1] : corner[2] ) );

    for ( let axis = 0; axis < 3; axis++ )
    {
      for ( let side = 0; side < 2; side++ )
      {
        for ( let a = 0; a < 2; a++ )
        {
          for ( let b = 0; b < 2; b++ )
          {
            const corner = [ 0, 0, 0 ];
            corner[axis] = side;
            corner[axis === 0 ? 1 : 0] = a;
            corner[axis === 2 ? 1 : 2] = b;

            for ( let c = 0; c < 3; c++ )
              positions.push( c === axis ? ( side ? hi[c] : lo[c] ) : ( corner[c] ? hi[c] - r : lo[c] + r ) );

            normals.push( axis === 0 ? ( side ? 1 : -1 ) : 0, axis === 1 ? ( side ? 1 : -1 ) : 0, axis === 2 ? ( side ? 1 : -1 ) : 0 );
          }
        }
      }
    }

    const indices: number[] = [];
    const center = [ ( x0 + x1 ) * 0.5, ( y0 + y1 ) * 0.5, ( z0 + z1 ) * 0.5 ];
    const outward = new THREE.Vector3();
    const e1 = new THREE.Vector3();
    const e2 = new THREE.Vector3();
    const at = ( i: number ) => new THREE.Vector3( positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2] );

    // Winds each triangle so its geometric normal points away from the box centre.
    const triangle = ( a: number, b: number, c: number ) =>
    {
      const pa = at( a );
      e1.subVectors( at( b ), pa );
      e2.subVectors( at( c ), pa );
      outward.set( pa.x - center[0], pa.y - center[1], pa.z - center[2] );
      if ( e1.cross( e2 ).dot( outward ) >= 0 )
        indices.push( a, b, c );
      else
        indices.push( a, c, b );
    };
    const quad = ( a: number, b: number, c: number, d: number ) =>
    {
      triangle( a, b, c );
      triangle( a, c, d );
    };

    // Faces.
    for ( let axis = 0; axis < 3; axis++ )
    {
      for ( let side = 0; side < 2; side++ )
      {
        if ( !outer[axis * 2 + side] )
          continue;
        const base = ( axis * 2 + side ) * 4;
        quad( base, base + 1, base + 3, base + 2 );
      }
    }

    // Edges: one quad between the two faces that share it, along the third axis.
    for ( let along = 0; along < 3; along++ )
    {
      const [ p, q ] = [ 0, 1, 2 ].filter( axis => axis !== along );
      for ( let sp = 0; sp < 2; sp++ )
      {
        for ( let sq = 0; sq < 2; sq++ )
        {
          if ( !outer[p * 2 + sp] && !outer[q * 2 + sq] )
            continue;
          const ends = [ 0, 1 ].map( s =>
          {
            const corner = [ 0, 0, 0 ];
            corner[p] = sp;
            corner[q] = sq;
            corner[along] = s;
            return corner;
          } );
          quad(
            vertex_index( p, sp, ends[0] ),
            vertex_index( p, sp, ends[1] ),
            vertex_index( q, sq, ends[1] ),
            vertex_index( q, sq, ends[0] )
          );
        }
      }
    }

    // Corners: the three face vertices that meet there.
    for ( let sx = 0; sx < 2; sx++ )
      for ( let sy = 0; sy < 2; sy++ )
        for ( let sz = 0; sz < 2; sz++ )
        {
          if ( !outer[sx] && !outer[2 + sy] && !outer[4 + sz] )
            continue;
          const corner = [ sx, sy, sz ];
          triangle( vertex_index( 0, sx, corner ), vertex_index( 1, sy, corner ), vertex_index( 2, sz, corner ) );
        }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute( "position", new THREE.Float32BufferAttribute( positions, 3 ) );
    geometry.setAttribute( "normal", new THREE.Float32BufferAttribute( normals, 3 ) );
    geometry.setIndex( indices );
    return geometry;
  }

  /** Vertex colors as normalized bytes: a quarter of the Float32 size, and plenty for a flat clay tint. */
  private static colorAttribute( count: number, colorAt: ( index: number ) => THREE.Color ): THREE.BufferAttribute
  {
    const colors = new Uint8Array( count * 3 );
    for ( let i = 0; i < count; i++ )
    {
      const color = colorAt( i );
      colors[i * 3] = Math.round( THREE.MathUtils.clamp( color.r, 0, 1 ) * 255 );
      colors[i * 3 + 1] = Math.round( THREE.MathUtils.clamp( color.g, 0, 1 ) * 255 );
      colors[i * 3 + 2] = Math.round( THREE.MathUtils.clamp( color.b, 0, 1 ) * 255 );
    }
    return new THREE.Uint8BufferAttribute( colors, 3, true );
  }

  private static mortarCore( width: number, height: number ): THREE.BufferGeometry
  {
    const recess = BrickMasonry.MORTAR_RECESS;
    const geometry = new THREE.BoxGeometry(
      Math.max( width - recess * 2, recess ),
      Math.max( height - recess * 2, recess ),
      1 - recess * 2
    );
    // Bricks carry no UVs; merged parts must share one attribute set.
    geometry.deleteAttribute( "uv" );
    geometry.translate( width * 0.5, height * 0.5, 0.5 );

    const mortar = new THREE.Color().setHex( BrickMasonry.MORTAR );
    geometry.setAttribute( "color", BrickMasonry.colorAttribute( geometry.getAttribute( "position" ).count, () => mortar ) );
    return geometry;
  }

  private static seededRandom( seed: number ): () => number
  {
    let state = seed >>> 0;
    return () =>
    {
      state = ( state + 0x6d2b79f5 ) >>> 0;
      let t = state;
      t = Math.imul( t ^ ( t >>> 15 ), t | 1 );
      t ^= t + Math.imul( t ^ ( t >>> 7 ), t | 61 );
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }
}
